import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { SystemLockService } from './system-lock.service';

/**
 * Blocks state-changing REST calls (the file/translation upload+import endpoints under `/api/`) while
 * saving is locked — a publish is running or maintenance mode is on. GraphQL traffic goes over its own
 * transport and is gated by the Apollo lock link in graphql.provider.ts, so this interceptor leaves any
 * request whose URL contains `/graphql` alone (that includes the status query used to lift the lock).
 * Read-only methods (GET/HEAD/OPTIONS) always pass through.
 */
@Injectable()
export class SystemLockInterceptor implements HttpInterceptor {
  private readonly lock = inject(SystemLockService);

  private static readonly WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isWrite = SystemLockInterceptor.WRITE_METHODS.has(req.method.toUpperCase());
    const isGraphql = req.url.includes('/graphql');

    if (isWrite && !isGraphql && this.lock.isLocked()) {
      return throwError(
        () => new Error(this.lock.lockReason() ?? 'Changes are temporarily disabled.')
      );
    }

    return next.handle(req);
  }
}
